import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  HeartPulse, Sparkles, CheckCircle2, AlertTriangle, RefreshCw, Phone,
  LayoutGrid, ShieldCheck, Calendar, Printer, BadgeAlert,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { DailyCheckinWidget } from '../components/brand/DailyCheckinWidget';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /patient/care-plan — Blueprint Decision #34.
 *
 * Generative 7-day care plan from a primary diagnosis. The patient (or
 * a caregiver) picks the diagnosis + severity; LLM returns a structured
 * weekly plan + red flags + escalation path. Plan is persisted; latest
 * `active` plan loads on revisit.
 */
export default function PatientCarePlan() {
  const { user, getAuthHeader } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    primary_diagnosis: '',
    severity: 'moderate',
    chief_complaint: '',
    caregiver_role: '',
    known_limitations: '',
  });

  const patientId = user?.id;

  const loadActive = useCallback(async () => {
    if (!patientId) return;
    try {
      const { data } = await axios.get(`${API}/patient/${patientId}/care-plan`, getAuthHeader());
      if (data.found) setPlan(data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [patientId, getAuthHeader]);

  useEffect(() => { loadActive(); }, [loadActive]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const generate = async () => {
    if (!form.primary_diagnosis.trim()) {
      toast.error('Please enter a primary diagnosis.');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await axios.post(
        `${API}/patient/${patientId}/care-plan/generate`,
        form,
        { ...getAuthHeader(), timeout: 75000 },
      );
      setPlan(data);
      setShowForm(false);
      toast.success('New care plan generated.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate plan');
    } finally {
      setGenerating(false);
    }
  };

  const acknowledge = async () => {
    if (!plan?.id) return;
    try {
      await axios.post(`${API}/patient/${patientId}/care-plan/${plan.id}/acknowledge`, {}, getAuthHeader());
      toast.success('Plan acknowledged.');
      setPlan({ ...plan, patient_acknowledged_at: new Date().toISOString() });
    } catch (e) {
      toast.error('Could not acknowledge');
    }
  };

  const navItems = [
    { icon: HeartPulse, label: 'My Care Plan', active: true },
    { icon: Calendar,   label: 'Appointments',  href: '/patient/dashboard' },
    { icon: LayoutGrid, label: 'Dashboard',     href: '/patient/dashboard' },
    { icon: ShieldCheck, label: 'Trust Portal', href: '/trust' },
  ];

  const p = plan?.plan;

  return (
    <DashboardShell
      navItems={navItems}
      title="My Care Plan"
      subtitle="A 7-day self-care plan generated for your diagnosis. Review it with your care team."
      testId="patient-care-plan-page"
      rightSlot={
        <div className="flex gap-2">
          {plan && (
            <Button onClick={() => window.print()} className="cn-btn cn-btn-ghost" data-testid="care-plan-print">
              <Printer className="w-4 h-4" /> Print
            </Button>
          )}
          <Button onClick={() => setShowForm((s) => !s)} className="cn-btn cn-btn-primary" data-testid="care-plan-toggle-form">
            <Sparkles className="w-4 h-4" />
            {showForm ? 'Hide form' : plan ? 'New plan' : 'Create plan'}
          </Button>
        </div>
      }
    >
      {/* FORM */}
      {(showForm || !plan) && !loading && (
        <div className="cn-card p-6 mb-6" data-testid="care-plan-form">
          <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-4">Generate a new care plan</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="cp-diag">Primary diagnosis</label>
              <input id="cp-diag" className="cn-input w-full" placeholder="e.g. Congestive heart failure" value={form.primary_diagnosis} onChange={update('primary_diagnosis')} data-testid="care-plan-diag" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="cp-severity">Severity</label>
              <select id="cp-severity" className="cn-input w-full" value={form.severity} onChange={update('severity')} data-testid="care-plan-severity">
                <option value="mild">Mild</option>
                <option value="moderate">Moderate</option>
                <option value="severe">Severe</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="cp-caregiver">Caregiver role (optional)</label>
              <input id="cp-caregiver" className="cn-input w-full" placeholder="self / spouse / adult child" value={form.caregiver_role} onChange={update('caregiver_role')} data-testid="care-plan-caregiver" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="cp-cc">Chief complaint (optional)</label>
              <input id="cp-cc" className="cn-input w-full" placeholder="e.g. Shortness of breath climbing stairs" value={form.chief_complaint} onChange={update('chief_complaint')} data-testid="care-plan-cc" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="cp-limits">Known limitations (optional)</label>
              <input id="cp-limits" className="cn-input w-full" placeholder="mobility / hearing / vision / language" value={form.known_limitations} onChange={update('known_limitations')} data-testid="care-plan-limits" />
            </div>
          </div>
          <Button onClick={generate} disabled={generating} className="cn-btn cn-btn-primary mt-5" data-testid="care-plan-generate">
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating (8-15s)…' : 'Generate plan'}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="cn-card h-48 animate-pulse" />
      ) : !plan ? (
        <div className="cn-card p-10 text-center" data-testid="care-plan-empty">
          <HeartPulse className="w-12 h-12 text-cn-text-muted/40 mx-auto mb-4" />
          <h2 className="font-display font-semibold text-cn-text dark:text-white text-xl">No active care plan</h2>
          <p className="text-sm text-cn-text-muted mt-2 max-w-md mx-auto">
            Generate a care plan above to get a personalized 7-day self-care checklist.
          </p>
        </div>
      ) : (
        <>
          {/* HEADER */}
          <div className="cn-card p-6 mb-5" data-testid="care-plan-header">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="cn-eyebrow mb-1">{plan.severity} · generated {new Date(plan.generated_at).toLocaleDateString()}</div>
                <h2 className="font-display font-bold text-2xl text-cn-text dark:text-white">{plan.primary_diagnosis}</h2>
                <p className="mt-3 text-sm text-cn-text leading-relaxed max-w-3xl" data-testid="care-plan-summary">
                  {p?.summary}
                </p>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                {plan.ai_unavailable ? (
                  <span className="cn-trust-badge text-amber-700 border-amber-200 bg-amber-50">
                    <BadgeAlert className="w-3 h-3" /> Heuristic fallback
                  </span>
                ) : (
                  <span className="cn-trust-badge text-emerald-700 border-emerald-200 bg-emerald-50">
                    <CheckCircle2 className="w-3 h-3" /> LLM-generated
                  </span>
                )}
                {plan.patient_acknowledged_at ? (
                  <span className="text-[11px] text-cn-text-muted inline-flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    Acknowledged {new Date(plan.patient_acknowledged_at).toLocaleDateString()}
                  </span>
                ) : (
                  <Button onClick={acknowledge} className="cn-btn cn-btn-ghost !py-2 !px-3" data-testid="care-plan-acknowledge">
                    Mark as read
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* DAILY CHECK-IN — Blueprint #34 enhancement */}
          <DailyCheckinWidget
            patientId={patientId}
            planId={plan.id}
            planStartedAt={plan.generated_at}
            getAuthHeader={getAuthHeader}
          />

          {/* WEEKLY PLAN */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 mb-6" data-testid="care-plan-weekly">
            {(p?.weekly_plan || []).map((d, i) => (
              <div key={i} className="cn-card p-4" data-testid={`care-plan-day-${i}`}>
                <div className="font-display font-bold text-cn-primary mb-2">{d.label || `Day ${d.day}`}</div>
                <ul className="space-y-1.5 mb-3">
                  {(d.action_items || []).map((a, j) => (
                    <li key={j} className="text-xs text-cn-text flex items-start gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-cn-primary mt-1.5 shrink-0" />
                      <span>{a}</span>
                    </li>
                  ))}
                </ul>
                {d.vitals_targets?.length > 0 && (
                  <div className="border-t border-cn-border pt-2">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Vitals targets</div>
                    <ul className="space-y-0.5">
                      {d.vitals_targets.map((v, j) => (
                        <li key={j} className="text-[11px] text-cn-text-muted">{v}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* RED FLAGS */}
          <div className="cn-card p-6 border-amber-200 bg-amber-50/30 mb-5" data-testid="care-plan-red-flags">
            <h3 className="font-display font-semibold text-lg text-amber-800 mb-3 inline-flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" /> Red flags — call 911 or your care team
            </h3>
            <ul className="grid sm:grid-cols-2 gap-2">
              {(p?.red_flags || []).map((r, i) => (
                <li key={i} className="text-sm text-amber-900 flex items-start gap-2" data-testid={`care-plan-red-flag-${i}`}>
                  <Phone className="w-3.5 h-3.5 text-amber-600 mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ESCALATION PATH */}
          <div className="cn-card p-6" data-testid="care-plan-escalation">
            <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-3">When to escalate</h3>
            <ol className="space-y-2.5">
              {(p?.escalation_path || []).map((step, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`care-plan-escalation-${i}`}>
                  <span className="w-6 h-6 rounded-full bg-cn-primary text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                  <span className="text-sm text-cn-text">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <p className="text-[11px] text-cn-text-muted mt-5" data-testid="care-plan-disclaimer">
            This plan is educational decision support — it does NOT replace clinical advice.
            Always confirm changes to medications, activity, or diet with your assigned clinician.
          </p>
        </>
      )}
    </DashboardShell>
  );
}
